import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { XMarkIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import {
  getAvailableProvisioningAccounts,
  provisionProject,
} from '../../../services/projectService';

const STEPS = {
  form: 'form',
  running: 'running',
  done: 'done',
  error: 'error',
};

const AutoProvisionModal = ({ isOpen, onClose, onProvisioned }) => {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [botToken, setBotToken] = useState(''); // optional override
  const [useManualToken, setUseManualToken] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [step, setStep] = useState(STEPS.form);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const accountsQuery = useQuery({
    queryKey: ['provisioning-accounts'],
    queryFn: getAvailableProvisioningAccounts,
    enabled: isOpen,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDisplayName('');
      setBotToken('');
      setUseManualToken(false);
      setAccountId('');
      setStep(STEPS.form);
      setResult(null);
      setErrorMessage('');
    }
  }, [isOpen]);

  const provisionMutation = useMutation({
    mutationFn: provisionProject,
    onSuccess: (response) => {
      if (response && response.success) {
        setResult(response.data);
        setStep(STEPS.done);
        if (typeof onProvisioned === 'function') {
          onProvisioned(response.data);
        }
      } else {
        setErrorMessage(response && response.error ? response.error : 'Provisioning failed');
        setStep(STEPS.error);
      }
    },
    onError: (err) => {
      const detail =
        err?.response?.data?.error ||
        err?.message ||
        'Unknown error';
      const code = err?.response?.data?.code;
      setErrorMessage(code ? `${code}: ${detail}` : detail);
      setStep(STEPS.error);
    },
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (useManualToken && !botToken.trim()) return;
    setStep(STEPS.running);
    const payload = {
      name: name.trim(),
      display_name: displayName.trim() || name.trim(),
      account_id: accountId ? Number(accountId) : undefined,
    };
    if (useManualToken && botToken.trim()) {
      payload.bot_token = botToken.trim();
    }
    provisionMutation.mutate(payload);
  };

  const accounts = accountsQuery.data?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <RocketLaunchIcon className="h-6 w-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Auto-provision new project
          </h2>
        </div>

        {step === STEPS.form && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
              A farm account will register a new bot via @BotFather, create a
              forum group, promote the bot to admin, and create the first topic.
              Nothing to paste from BotFather.
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Brand X"
                className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topic display name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Competitors (defaults to project name)"
                className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Owner account (optional)
              </label>
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Auto-pick (round-robin)</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    #{a.id} — {a.name} (recent: {a.recent_provisions})
                    {a.is_reserved ? ' [reserve]' : ''}
                  </option>
                ))}
              </select>
              {accountsQuery.isLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading accounts...</p>
              )}
              {accounts.length === 0 && !accountsQuery.isLoading && (
                <p className="text-xs text-amber-600 mt-1">
                  No healthy farm accounts available right now.
                </p>
              )}
            </div>

            <div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={useManualToken}
                  onChange={(e) => setUseManualToken(e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                I already have a bot token (skip BotFather)
              </label>
            </div>

            {useManualToken && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bot token
                </label>
                <input
                  type="text"
                  value={botToken}
                  onChange={(e) => setBotToken(e.target.value)}
                  placeholder="123456:ABC-DEF..."
                  className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!name.trim() || (useManualToken && !botToken.trim())}
                className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Provision
              </button>
            </div>
          </form>
        )}

        {step === STEPS.running && (
          <div className="py-8 flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600" />
            <p className="text-sm text-gray-700">
              {useManualToken
                ? 'Creating forum group and topic...'
                : 'Talking to @BotFather, creating forum group and topic...'}
            </p>
            <p className="text-xs text-gray-500">
              This usually takes 15-45 seconds.
            </p>
          </div>
        )}

        {step === STEPS.done && result && (
          <div className="space-y-3">
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              ✓ Project created.
            </div>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between">
                <dt className="text-gray-500">Project ID</dt>
                <dd className="font-mono">{result.project?.id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Report group</dt>
                <dd className="font-mono">{result.report_group_id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Topic ID</dt>
                <dd className="font-mono">{result.report_topic_id}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Owner account</dt>
                <dd className="font-mono">{result.owner_account_id}</dd>
              </div>
            </dl>
            {result.invite_link && (
              <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
                <div className="text-gray-500 mb-1">Invite link</div>
                <div className="flex items-center gap-2">
                  <a
                    href={result.invite_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline truncate"
                    title={result.invite_link}
                  >
                    {result.invite_link}
                  </a>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(result.invite_link)}
                    className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              {result.invite_link && (
                <a
                  href={result.invite_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
                >
                  Join group
                </a>
              )}
              {result.report_group_id && result.report_topic_id && (
                <a
                  href={`https://t.me/c/${String(result.report_group_id).replace('-100', '')}/${result.report_topic_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Open topic
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {step === STEPS.error && (
          <div className="space-y-3">
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              ✗ {errorMessage}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStep(STEPS.form)}
                className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoProvisionModal;
